/**
 * Input Validation Utilities
 * Validates poster IDs, video URLs, and camera permissions
 */

/**
 * Custom validation error class
 */
export class ValidationError extends Error {
  constructor(message, field) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
  }
}

/**
 * Validate poster ID format (UUID v4)
 * @param {string} posterId - Poster ID to validate
 * @returns {boolean} - True if valid
 * @throws {ValidationError} - If invalid
 */
export function validatePosterId(posterId) {
  if (!posterId || typeof posterId !== 'string') {
    throw new ValidationError('Poster ID is required', 'posterId');
  }

  // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  // Also accept poster_prefix format: poster_uuid
  const posterIdRegex =
    /^poster_[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  if (!uuidV4Regex.test(posterId) && !posterIdRegex.test(posterId)) {
    throw new ValidationError('Invalid poster ID format (expected UUID v4)', 'posterId');
  }

  return true;
}

/**
 * Validate video URL format (must be HTTPS)
 * @param {string} videoUrl - Video URL to validate
 * @returns {boolean} - True if valid
 * @throws {ValidationError} - If invalid
 */
export function validateVideoUrl(videoUrl) {
  if (!videoUrl || typeof videoUrl !== 'string') {
    throw new ValidationError('Video URL is required', 'videoUrl');
  }

  try {
    const url = new URL(videoUrl);

    // Must be HTTPS for security
    if (url.protocol !== 'https:') {
      throw new ValidationError('Video URL must use HTTPS protocol', 'videoUrl');
    }

    // Check file extension (mp4, webm)
    const validExtensions = ['.mp4', '.webm'];
    const hasValidExtension = validExtensions.some((ext) => url.pathname.endsWith(ext));

    if (!hasValidExtension) {
      throw new ValidationError('Video URL must end with .mp4 or .webm', 'videoUrl');
    }

    return true;
  } catch (error) {
    if (error instanceof ValidationError) {
      throw error;
    }
    throw new ValidationError('Invalid video URL format', 'videoUrl');
  }
}

/**
 * Validate camera permission status
 * @returns {Promise<boolean>} - True if camera permission granted
 * @throws {ValidationError} - If permission denied or not available
 */
export async function validateCameraPermission() {
  // Check if mediaDevices API is available
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    throw new ValidationError('Camera access is not supported in this browser', 'cameraPermission');
  }

  try {
    // Check permission state (if Permissions API available)
    if (navigator.permissions && navigator.permissions.query) {
      const result = await navigator.permissions.query({ name: 'camera' });

      if (result.state === 'denied') {
        throw new ValidationError('Camera permission denied', 'cameraPermission');
      }

      if (result.state === 'granted') {
        return true;
      }

      // state === 'prompt' - will request permission
    }

    // If Permissions API not available or state is 'prompt', try to get camera access
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' },
    });

    // Release the stream immediately
    stream.getTracks().forEach((track) => track.stop());

    return true;
  } catch (error) {
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      throw new ValidationError('Camera permission denied by user', 'cameraPermission');
    }

    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      throw new ValidationError('No camera device found', 'cameraPermission');
    }

    if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      throw new ValidationError('Camera is already in use', 'cameraPermission');
    }

    // Re-throw validation errors
    if (error instanceof ValidationError) {
      throw error;
    }

    // Unknown error
    throw new ValidationError(`Camera permission error: ${error.message}`, 'cameraPermission');
  }
}

/**
 * Validate session ID format (UUID v4)
 * @param {string} sessionId - Session ID to validate
 * @returns {boolean} - True if valid
 * @throws {ValidationError} - If invalid
 */
export function validateSessionId(sessionId) {
  if (!sessionId || typeof sessionId !== 'string') {
    throw new ValidationError('Session ID is required', 'sessionId');
  }

  // UUID v4 format or session_uuid format
  const sessionIdRegex =
    /^(session_)?[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  if (!sessionIdRegex.test(sessionId)) {
    throw new ValidationError('Invalid session ID format (expected UUID v4)', 'sessionId');
  }

  return true;
}
